import { materialTypeFormat } from '@/common/material-type-formatting';
import { useGetUploadUrlMutation } from '@/common/utils/graphql.generated';
import { useFacility } from '@/contexts/facility-context/hook';
import { useCurrentUser } from '@/hooks';
import { Button, Flex, Image, LoadingOverlay, Stack, Text, Title } from '@mantine/core';
import { IconArrowLeft, IconCheck, IconTrash } from '@tabler/icons-react';
import { useCallback, useState } from 'react';
import { v4 as randomUUID } from 'uuid';
import { MaterialType, MediaType } from '../../../../../../../backend/src/types.generated';
import { useFacilityBaleCreateMutation, useLinkPhotoToBaleMutation } from '../graphql.generated';
import { useBaleCreateContext } from '../hook';
import styles from '../styles.module.css';

export const BaleCreateConfirmPage = () => {
	const { setPage, photo, currentBale, resetWorkflow } = useBaleCreateContext();
	const { currentUser } = useCurrentUser();
	const { currentFacility } = useFacility();
	const [linkPhotoToBale] = useLinkPhotoToBaleMutation();
	const [facilityBaleCreate] = useFacilityBaleCreateMutation();
	const [getUploadUrl] = useGetUploadUrlMutation();
	const [uploading, setUploading] = useState(false);
	const [progress, setProgress] = useState(0);

	const handleDiscard = useCallback(() => {
		resetWorkflow();
	}, [resetWorkflow]);

	const handleConfirm = useCallback(async () => {
		if (!photo || !currentFacility || !currentBale?.material || !currentUser) return;
		setUploading(true);
		setProgress(0);

		try {
			// Generate S3 key with folder structure for bale photos
			const s3Key = `trackable-assets/bales/${randomUUID()}.jpg`;

			setProgress(10);

			// Get upload URL from backend
			const { data } = await getUploadUrl({
				variables: { key: s3Key },
			});

			if (!data?.getUploadUrl) {
				throw new Error('Failed to get upload URL');
			}

			setProgress(30);

			// Convert base64 to ArrayBuffer
			const photoBytes = await fetch(photo).then((res) => res.arrayBuffer());

			setProgress(60);

			// Upload to S3
			const response = await fetch(data.getUploadUrl.url, {
				method: 'PUT',
				body: photoBytes,
				headers: {
					'Content-Type': 'image/jpeg',
				},
			});

			if (!response.ok) {
				throw new Error('Failed to upload photo');
			}

			setProgress(100);
			const { data: scanEvent } = await facilityBaleCreate({
				variables: {
					input: {
						facilityId: currentFacility.id,
						material: currentBale.material,
						scannableCode: currentBale.code,
					},
				},
			});
			if (scanEvent?.facilityBaleCreate) {
				const res = await linkPhotoToBale({
					variables: {
						input: {
							scanEvent: {
								id: scanEvent.facilityBaleCreate.id,
							},
							photo: {
								filename: data.getUploadUrl.filename,
								type: MediaType.Image,
							},
							createdByApplicationType: 'mrf',
						},
					},
				});

				if (res.data?.createScanEventPhoto) {
					setPage('SUCCESS');
				}
			}
		} catch (error) {
			console.error('Error uploading photo:', error);
			// TODO: Show error message to user
		} finally {
			setUploading(false);
		}
	}, [
		currentBale?.code,
		currentBale?.material,
		currentFacility,
		currentUser,
		facilityBaleCreate,
		getUploadUrl,
		linkPhotoToBale,
		photo,
		setPage,
	]);

	// Get dynamic font size class based on bag count
	const getBagCountTextClass = () => {
		const bagCount = currentBale?.bags ?? 0;
		if (bagCount >= 10000) return styles.bagCountTextVeryLarge;
		if (bagCount >= 1000) return styles.bagCountTextLarge;
		return styles.bagCountText;
	};

	return (
		<Flex pos="relative" h="100vh" w="100%" className={styles.pageBackground} direction="column">
			<LoadingOverlay visible={uploading} overlayProps={{ blur: 2 }} />

			{/* Header */}
			<Flex
				className={styles.confirmHeader}
				pos="fixed"
				top={0}
				left={0}
				px="md"
				py="lg"
				bg="white"
				w="100%"
				align="center"
				gap="sm"
			>
				<IconArrowLeft onClick={() => setPage('PHOTO')} className={styles.clickable} />
				<Title order={2}>Confirm Bale Creation</Title>
			</Flex>

			{/* Content */}
			<Flex
				direction="column"
				p="md"
				pt="80px"
				pb="120px"
				gap="md"
				className={styles.contentContainer}
			>
				{/* Photo */}
				{photo && (
					<Stack gap="xs">
						<Text fw={600} size="lg">
							Photo
						</Text>
						<Image src={photo} alt="Bale photo" radius="md" className={styles.photoImage} />
					</Stack>
				)}

				{/* Bale Information */}
				<Stack gap="md">
					<Text fw={600} size="lg">
						Bale Information
					</Text>

					<Flex gap="md" w="100%">
						{/* Material Type Card */}
						<Flex
							direction="column"
							align="center"
							justify="center"
							flex={1}
							p="xl"
							className={styles.materialCard}
						>
							<Text className={styles.emojiLarge} mb="xs">
								{materialTypeFormat[currentBale?.material || MaterialType.SoftPlastic].emoji}
							</Text>
							<Text size="lg" fw={700} ta="center" lh={1.2} mb={4}>
								{materialTypeFormat[currentBale?.material || MaterialType.SoftPlastic].label}
							</Text>
							<Text size="sm" c="dimmed" fw={500} ta="center">
								Material Type
							</Text>
						</Flex>

						{/* Bag Count Card */}
						<Flex
							direction="column"
							align="center"
							justify="center"
							flex={1}
							p="xl"
							className={styles.materialCard}
						>
							<Text className={styles.emojiLarge} mb="xs">
								📋
							</Text>
							<Text fw={700} ta="center" lh={1.2} mb={4} className={getBagCountTextClass()}>
								{currentBale?.bags?.toLocaleString()}
							</Text>
							<Text size="sm" c="dimmed" fw={500} ta="center">
								Baled Bags
							</Text>
						</Flex>
					</Flex>
				</Stack>
			</Flex>

			{/* Bottom Buttons */}
			<Flex
				className={styles.confirmBottomButtons}
				pos="fixed"
				bottom={0}
				left={0}
				p="md"
				bg="white"
				w="100%"
				gap="sm"
			>
				<Button
					onClick={handleDiscard}
					variant="outline"
					color="red"
					size="lg"
					leftSection={<IconTrash size={16} />}
					fullWidth
				>
					Discard
				</Button>
				<Button
					onClick={handleConfirm}
					variant="filled"
					color="green"
					size="lg"
					leftSection={<IconCheck size={16} />}
					fullWidth
					disabled={!photo || uploading}
					loading={uploading}
				>
					{uploading ? `Uploading... ${progress}%` : 'Confirm'}
				</Button>
			</Flex>
		</Flex>
	);
};
